import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import { getAdminBookings, getAdminCustomers, type AdminBooking, type BookingCustomer } from '../../lib/booking-api';

export default function AdminCustomers() {
  const [customers, setCustomers] = useState<BookingCustomer[]>([]);
  const [bookings, setBookings] = useState<AdminBooking[]>([]);
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    Promise.all([getAdminCustomers(), getAdminBookings()])
      .then(([customerRows, bookingRows]) => {
        setCustomers(customerRows);
        setBookings(bookingRows);
        setSelectedId(customerRows[0]?.id || '');
      })
      .catch((reason) => setError(reason instanceof Error ? reason.message : 'Unable to load customers.'))
      .finally(() => setLoading(false));
  }, []);

  const bookingCount = (customerId: string) => bookings.filter((booking) => booking.customer_id === customerId).length;
  const returningCustomers = customers.filter((customer) => bookingCount(customer.id) > 1).length;
  const visible = useMemo(() => customers.filter((customer) => `${customer.full_name} ${customer.whatsapp} ${customer.email || ''}`.toLowerCase().includes(query.toLowerCase())), [customers, query]);
  const selected = customers.find((customer) => customer.id === selectedId) ?? visible[0];
  const selectedBookings = selected ? bookings.filter((booking) => booking.customer_id === selected.id) : [];
  const confirmedSpend = selectedBookings
    .filter((booking) => ['confirmed', 'arrived', 'completed'].includes(booking.status))
    .reduce((sum, booking) => sum + booking.service_price, 0);

  function exportList() {
    const csv = [
      'Name,WhatsApp,Email,Shape,Bookings,Created',
      ...visible.map((customer) => [customer.full_name, customer.whatsapp, customer.email || '', customer.preferred_shape || '', bookingCount(customer.id), customer.created_at]
        .map((value) => `"${String(value).replaceAll('"', '""')}"`).join(',')),
    ].join('\n');
    const link = document.createElement('a');
    link.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    link.download = 'mf-nailedit-customers.csv';
    link.click();
    URL.revokeObjectURL(link.href);
  }

  return (
    <AdminLayout title="Customers" description="One recognized profile per customer, shared across every booking." actions={<button className="studio-admin-btn" onClick={exportList} disabled={!visible.length}>Export list</button>}>
      {error && <p className="studio-admin-error" role="alert">{error}</p>}
      <section className="studio-admin-grid metrics">
        <article className="studio-admin-metric"><span>Unique customers</span><strong>{loading ? '—' : customers.length}</strong><small>Recognized profiles</small></article>
        <article className="studio-admin-metric"><span>Total booking requests</span><strong>{loading ? '—' : bookings.length}</strong><small>All statuses</small></article>
        <article className="studio-admin-metric"><span>Returning customers</span><strong>{loading ? '—' : returningCustomers}</strong><small>More than one booking</small></article>
        <article className="studio-admin-metric"><span>Pending review</span><strong>{loading ? '—' : bookings.filter((booking) => booking.status === 'pending_approval').length}</strong><small>Needs action</small></article>
      </section>
      <section className="studio-admin-card studio-admin-grid">
        <div>
          <p className="admin-eyebrow">CUSTOMER IDENTITY</p>
          <h2>Repeat bookings stay under one profile.</h2>
          <p>WhatsApp is the primary matching key. Matching name and email provide a guarded secondary recognition path.</p>
        </div>
        <label className="studio-admin-field">Search customers<input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Name, WhatsApp or email" /></label>
        <div className="studio-admin-table">
          {loading ? <p>Loading customers…</p> : visible.length ? visible.map((customer) => (
            <button key={customer.id} type="button" className={`studio-admin-row${selected?.id === customer.id ? ' selected' : ''}`} onClick={() => setSelectedId(customer.id)} style={{ textAlign: 'left', cursor: 'pointer' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr auto', gap: 14, alignItems: 'center' }}>
                <div><strong>{customer.full_name}</strong><small style={{ display: 'block' }}>{customer.whatsapp}</small></div>
                <div><strong>{customer.preferred_shape || 'Shape not supplied'}</strong><small style={{ display: 'block' }}>{customer.email || 'No email'}</small></div>
                <div><strong>{bookingCount(customer.id)} booking(s)</strong><small style={{ display: 'block' }}>Updated {new Date(customer.updated_at || customer.created_at).toLocaleDateString()}</small></div>
                <span className="studio-admin-status">{bookingCount(customer.id) > 1 ? 'Returning' : 'Customer'}</span>
              </div>
            </button>
          )) : <p>No customers yet.</p>}
        </div>
      </section>
      {selected && (
        <section className="studio-admin-card studio-admin-grid">
          <div><p className="admin-eyebrow">SELECTED CUSTOMER</p><h2>{selected.full_name}</h2><p>{selected.whatsapp} • {selected.email || 'No email supplied'}</p></div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,minmax(0,1fr))', gap: 14 }}>
            <div><small>PREFERRED SHAPE</small><p>{selected.preferred_shape || 'Not supplied'}</p></div>
            <div><small>BOOKINGS</small><p>{selectedBookings.length}</p></div>
            <div><small>RECORDED SERVICE VALUE</small><p>₦{confirmedSpend.toLocaleString('en-NG')}</p></div>
          </div>
          <div><small>NOTES</small><p>{selected.notes || 'No notes supplied.'}</p></div>
          <div className="studio-admin-actions">
            <a className="studio-admin-btn primary" href={`https://wa.me/${selected.whatsapp_normalized || `234${selected.whatsapp.replace(/\D/g, '').replace(/^0/, '')}`}`} target="_blank" rel="noreferrer">Open WhatsApp</a>
            <Link className="studio-admin-btn" to={`/admin/bookings?create=1&customer=${selected.id}`}>Create booking</Link>
            <Link className="studio-admin-btn" to={`/admin/bookings?customer=${selected.id}`}>View bookings</Link>
          </div>
        </section>
      )}
    </AdminLayout>
  );
}
